<script setup>
import OnboardingFeatureCard from './OnboardingFeatureCard.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

const getters = useStoreGetters();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);
const currentUser = computed(() => getters.getCurrentUser.value);

const currentIndex = ref(0);

const features = computed(() => [
  {
    imageSrc: '/dashboard/images/onboarding/omnichannel-inbox.png',
    imageAlt: 'Omnichannel',
    to: 'settings_inbox_new',
    title: t('ONBOARDING.ALL_CONVERSATION.TITLE'),
    description: t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION'),
    linkText: t('ONBOARDING.ALL_CONVERSATION.NEW_LINK'),
  },
  {
    imageSrc: '/dashboard/images/onboarding/teams.png',
    imageAlt: 'Teams',
    to: 'settings_teams_new',
    title: t('ONBOARDING.TEAM_MEMBERS.TITLE'),
    description: t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION'),
    linkText: t('ONBOARDING.TEAM_MEMBERS.NEW_LINK'),
  },
  {
    imageSrc: '/dashboard/images/onboarding/canned-responses.png',
    imageAlt: 'Canned responses',
    to: 'canned_list',
    title: t('ONBOARDING.CANNED_RESPONSES.TITLE'),
    description: t('ONBOARDING.CANNED_RESPONSES.DESCRIPTION'),
    linkText: t('ONBOARDING.CANNED_RESPONSES.NEW_LINK'),
  },
  {
    imageSrc: '/dashboard/images/onboarding/labels.png',
    imageAlt: 'Labels',
    to: 'labels_list',
    title: t('ONBOARDING.LABELS.TITLE'),
    description: t('ONBOARDING.LABELS.DESCRIPTION'),
    linkText: t('ONBOARDING.LABELS.NEW_LINK'),
  },
]);

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  let translationKey;
  if (hours < 12) {
    translationKey = 'ONBOARDING.GREETING_MORNING';
  } else if (hours < 18) {
    translationKey = 'ONBOARDING.GREETING_AFTERNOON';
  } else {
    translationKey = 'ONBOARDING.GREETING_EVENING';
  }
  return t(translationKey, {
    name: currentUser.value.name,
    installationName: globalConfig.value.installationName,
  });
});

const goToPrev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  }
};

const goToNext = () => {
  if (currentIndex.value < features.value.length - 1) {
    currentIndex.value++;
  }
};

const goToSlide = (index) => {
  currentIndex.value = index;
};
</script>

<template>
  <div
    class="min-h-screen mx-auto flex flex-col gap-8 p-8 w-full font-inter overflow-hidden"
  >
    <!-- Header -->
    <div class="w-full mt-3 text-center">
      <p
        class="text-xl font-semibold text-slate-900 dark:text-white font-interDisplay tracking-[0.3px]"
      >
        {{ greetingMessage }}
      </p>
      <p class="text-slate-600 dark:text-slate-400 max-w-2xl text-base">
        {{
          $t('ONBOARDING.DESCRIPTION', {
            installationName: globalConfig.installationName,
          })
        }}
      </p>
    </div>

    <!-- Carousel Container -->
    <div class="relative flex-1 flex items-center justify-center">
      <!-- Previous Button -->
      <button
        @click="goToPrev"
        :disabled="currentIndex === 0"
        class="absolute left-0 z-20 px-3 py-2 text-n-gray-5 hover:bg-n-gray-1 dark:text-white rounded-md bg-transparent dark:hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        :class="{ 'opacity-0 pointer-events-none': currentIndex === 0 }"
      >
        <lucide-icon name="chevron-left" size="20" />
      </button>

      <!-- Cards Container -->
      <div class="relative w-full max-w-3xl h-[400px] mx-20" style="perspective: 2000px;">
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="absolute top-1/2 left-1/2 w-[500px] h-full transition-all duration-500 ease-out"
          :style="{
            transform: `translate(-50%, -50%) translateX(${
              (index - currentIndex) * 320
            }px) translateZ(${
              index === currentIndex ? '0px' : '-150px'
            }) scale(${index === currentIndex ? 1 : 0.8}) rotateY(${
              index < currentIndex ? '25deg' : index > currentIndex ? '-25deg' : '0deg'
            })`,
            transformStyle: 'preserve-3d',
            zIndex: index === currentIndex ? 10 : 5 - Math.abs(index - currentIndex),
            opacity: Math.abs(index - currentIndex) > 1 ? 0 : index === currentIndex ? 1 : 0.7,
            pointerEvents: index === currentIndex ? 'auto' : 'none',
          }"
        >
          <OnboardingFeatureCard
            :image-src="feature.imageSrc"
            :image-alt="feature.imageAlt"
            :to="feature.to"
            :title="feature.title"
            :description="feature.description"
            :link-text="feature.linkText"
          />
        </div>
      </div>

      <!-- Next Button -->
      <button
        @click="goToNext"
        :disabled="currentIndex === features.length - 1"
        class="absolute right-0 z-20 px-3 py-2 text-n-gray-5 hover:bg-n-gray-1 dark:text-white rounded-md bg-transparent dark:hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        :class="{
          'opacity-0 pointer-events-none': currentIndex === features.length - 1,
        }"
      >
        <lucide-icon name="chevron-right" size="20"/>
      </button>
    </div>

    <!-- Dots Indicator -->
    <div class="flex justify-center gap-2 pb-8">
      <button
        v-for="(_, index) in features"
        :key="index"
        @click="goToSlide(index)"
        class="w-2 h-2 rounded-full transition-all"
        :class="
          index === currentIndex
            ? 'bg-white w-6'
            : 'bg-white/40 hover:bg-white/60'
        "
      />
    </div>
  </div>
</template>